import { useContext, useEffect, useState } from 'react';
import {
	graphqlFetch,
	assetsQueryDocument,
	type GetAssetsQuery,
} from '@shared/api';
import { QueryClient, QueryKey, useQuery, useQueryClient } from '@tanstack/react-query';

import { clone, Font, USER_ROLE } from '@/constants';
import { Logger } from '@shared/lib/Logger';
import { AppContext } from '@/App.context';
import { getQueryCacheOptions } from '@shared/query-client';
import { useIsOnline } from '@shared/lib/hooks';

const logger = new Logger('Assets', {
	log: 'rgb(50, 173, 230)',
	debug: 'rgb(0, 113, 164)',
	info: 'rgb(36, 138, 61)',
	error: 'rgb(255, 45, 85)',
});

const ASSETS_QUERY_KEY = ['assets'];

export function useAssets(): AssetsResponse {
	const queryKey = ASSETS_QUERY_KEY;
	const isOnline = useIsOnline();
	const queryClient = useQueryClient();
	const isAdmin = localStorage.getItem(USER_ROLE) === 'admin';
	const { parseGraphQLClientError } = useContext(AppContext);

	const cached: GetAssetsQuery | undefined = queryClient.getQueryData(queryKey);

	const [fonts, setFonts] = useState<Array<Font>>([]);
	// A previously cached empty fonts list means the last fetch was
	// incomplete/failed -- treat it as immediately stale instead of holding
	// onto it for the full TTL.
	const ttlMinutes = cached && !cached.fonts.length ? 0 : 30;
	const cacheOptions = getQueryCacheOptions({
		isOnline,
		isAdmin,
		ttlMinutes,
	});

	const { data, isPending, error } = useQuery<GetAssetsQuery>({
		queryKey,
		queryFn: async () =>
			getAssets({ queryKey, queryClient, isOnline }),
		staleTime: cacheOptions.staleTime,
		gcTime: cacheOptions.gcTime,
		retry: isOnline ? 3 : 0,
	});

	useEffect(() => {
		if (isPending) return;
		if (error) {
			console.error(error);
			return;
		}
		if (!data) return;

		const processedFonts = data.fonts.map(mapFontUrls);

		setFonts(processedFonts);
	}, [isPending, data, error]);
	return {
		fonts,
		isLoading: isPending,
		error: error ? parseGraphQLClientError(error) : null,
	};
}

async function getAssets({
	queryClient,
	queryKey,
	isOnline,
}: {
	queryClient: QueryClient;
	queryKey: QueryKey;
	isOnline: boolean;
}): Promise<GetAssetsQuery> {
	const cached: GetAssetsQuery | undefined = queryClient.getQueryData(queryKey);

	// The app is offline
	if (!isOnline) {
		if (!cached)
			return {
				fonts: [],
			};
		return cached;
	}


	return await graphqlFetch(assetsQueryDocument, undefined, { withAuth: false });
}

export function useLoadFonts(fonts: Array<Font>) {
	useEffect(() => {
		if (!fonts.length) return;
		const promises: Array<Promise<void>> = [];
		for (const font of fonts) {
			promises.push(loadFont(font));
		}
		Promise.all(promises)
			.then(() => {
				logger.info(`Success loading fonts`);
			})
			.catch(logger.error);
	}, [fonts]);
}

function mapFontUrls(f: Font) {
	const font = clone(f) as Font;
	font.urls = font.urls.map((url) =>
		url.replace(
			/(https:\/\/cdn\.pwa\.canvasflow\.io\/fonts)+/gim,
			'https://fonts.canvasflow.work',
		),
	);
	return font;
}

interface AssetsResponse {
	fonts: Array<Font>;
	isLoading: boolean;
	error: Error | null;
}

async function loadFont(font: Font): Promise<void> {
	const { name, urls } = font;
	const promises = [];

	for (const url of urls) {
		if (!/.woff2$/.test(url)) {
			continue;
		}
		// THIS DOWNLOADS THE FONT

		promises.push(
			new Promise<void>((resolve) => {
				const fontFace = new FontFace(
					name,
					`url(${url}) format("woff2")`,
				);
				fontFace
					.load()
					.then((loadedFace) => {
						document.fonts.add(loadedFace);
						resolve();
					})
					.catch((err) => {
						console.error(err);
						resolve();
					});
			}),
		);
	}
	await Promise.all(promises);
}
